import { Box, Button } from '@mui/material';
import { useRef, forwardRef, useImperativeHandle } from 'react';
import { observer } from 'mobx-react';

import FormHandler from './FormHandler';
import FieldGenerator from './FieldGenerator';
import useURLPaginate from '../../hooks/useURLPaginate';
import PAGINATION from './constants/pagination';
import { useLocalization } from '../../providers/LocalizationProvider';
import getDefaultValuesForForm from './lib/getDefaultValuesForForm';

function FormSearchButtons({
  onSubmitClick,
  onHideClick,
  onClearClick,
  IconLibrary = {},
}) {
  const { formatMessage } = useLocalization();
  return (
    <Box display="flex" justifyContent="center" gap={2} mt={3} mb={1}>
      <Button
        variant="contained"
        startIcon={<IconLibrary.Search />}
        color="primary"
        onClick={() => onSubmitClick()}>
        {formatMessage({
          id: 'FIND',
          defaultMessage: 'Find',
        })}
      </Button>

      <Button
        variant="contained"
        color="primary"
        startIcon={<IconLibrary.ClearSearch />}
        onClick={() => {
          onClearClick();
        }}>
        {formatMessage({
          id: 'CLEAR',
          defaultMessage: 'Clear',
        })}
      </Button>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<IconLibrary.HideSearch />}
        onClick={() => onHideClick()}>
        {formatMessage({
          id: 'HIDE',
          defaultMessage: 'Hide',
        })}
      </Button>
    </Box>
  );
}

const FormSearch = forwardRef(
  (
    {
      schema,
      onClear = undefined,
      onSubmit = undefined,
      hideSearchForm = undefined,
      validationSchema = null,
      customComponents = {},
      IconLibrary = {},
      children,
      getValueByControl = null,
    },
    ref,
  ) => {
    const formRef = useRef();

    const { filters } = useURLPaginate(PAGINATION);

    useImperativeHandle(ref, () => ({
      submit: () => {
        if (formRef.current) {
          formRef.current.submit();
        }
      },
    }));

    const defaultInitialValuesFromSchema = getDefaultValuesForForm({
      schema,
      ...(getValueByControl && { getValueByControl }),
    });

    const initialValues = { ...defaultInitialValuesFromSchema, ...filters };

    return (
      <Box key={JSON.stringify(filters || {})}>
        <FormHandler
          formSchema={schema}
          disableUnSavedCheck
          ref={formRef}
          validationSchema={validationSchema}
          initialValues={initialValues || {}}
          onSubmit={onSubmit}>
          <div className="form-fields">
            <FieldGenerator
              customComponents={customComponents}
              schema={schema}
            />
            {children || (
              <FormSearchButtons
                IconLibrary={IconLibrary}
                formRef={formRef}
                onClearClick={() => {
                  formRef.current?.reset();
                  onClear();
                }}
                onSubmitClick={() => formRef.current.submit()}
                onHideClick={() => {
                  if (hideSearchForm) {
                    hideSearchForm();
                  }
                }}
              />
            )}
          </div>
        </FormHandler>
      </Box>
    );
  },
);

export default observer(FormSearch);
